// src/hooks/useExamKiosk.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function useExamKiosk(opts = {}) {
    const {
        maxViolations = 6,
        storageKey = "exam:kiosk:default",
        debounceMs = 1200,
        heartbeatMs = 5000,

        // Cada cuánto volver a advertir si sigue fuera.
        // En Examen.js lo vamos a manejar con el contador visual.
        repeatStrikeMs = 4500,
        autoRepeatStrikes = true,

        onViolation,
        onTooManyViolations,
        onAttentionRequired,
        countPageHide = true,
        preventDefaultOnHotkeys = true,
    } = opts;

    const [blocked, setBlocked] = useState(false);

    // ✅ reason SOLO para bloqueo actual (overlay)
    const [blockedReason, setBlockedReason] = useState("");

    // ✅ última infracción real (para modal "Atención" / logs)
    const [lastViolationReason, setLastViolationReason] = useState("");

    const runningRef = useRef(false);

    const focusCheckTimerRef = useRef(null);

    const clearFocusCheckTimer = () => {
        try {
            if (focusCheckTimerRef.current) clearTimeout(focusCheckTimerRef.current);
        } catch { }
        focusCheckTimerRef.current = null;
    };

    const graceUntilRef = useRef(0);
    const grantGrace = useCallback((ms = 1500) => {
        graceUntilRef.current = Date.now() + Math.max(0, ms);
    }, []);

    const loadCount = useCallback(() => {
        try {
            const raw = sessionStorage.getItem(`${storageKey}:violations`);
            const n = Number(raw);
            return Number.isFinite(n) && n >= 0 ? n : 0;
        } catch {
            return 0;
        }
    }, [storageKey]);

    const saveCount = useCallback((n) => {
        try {
            sessionStorage.setItem(`${storageKey}:violations`, String(Math.max(0, n)));
        } catch { }
    }, [storageKey]);

    const countRef = useRef(0);
    const lastStrikeRef = useRef(0);

    useEffect(() => {
        countRef.current = loadCount();
    }, [loadCount]);

    const isFullscreen = () => {
        const domFS = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );

        const near = (a, b, tol = 2) => Math.abs(a - b) <= tol;
        const vpW = window.innerWidth || 0;
        const vpH = window.innerHeight || 0;
        const sW = (window.screen && (window.screen.availWidth || window.screen.width)) || 0;
        const sH = (window.screen && (window.screen.availHeight || window.screen.height)) || 0;
        const browserFS = vpW && vpH && sW && sH && near(vpW, sW) && near(vpH, sH);

        let displayModeFS = false;
        try {
            displayModeFS =
                (window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches) ||
                (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
        } catch { }

        return domFS || browserFS || displayModeFS;
    };

    const computeBlocked = () => {
        const noFocus = !document.hasFocus?.() || document.hidden === true;
        const noFs = !isFullscreen();
        return noFocus || noFs;
    };

    const computeReason = () => {
        // prioridad: pestaña oculta > blur/foco > fullscreen
        // (si está hidden, casi siempre ya no hay foco también)
        if (document.hidden) return "visibility_hidden";

        const hasFocus = document.hasFocus?.() ?? true;
        if (!hasFocus) return "window_blur";

        if (!isFullscreen()) return "fullscreen_exit";

        // fallback
        return "attention_required";
    };

    const lastRepeatStrikeRef = useRef(0);

    // ✅ SOLO sincroniza blocked. NO pisa razones.
    const applyBlocked = useCallback(() => {
        const b = computeBlocked();
        setBlocked(b);

        // Si ya no está bloqueado, limpia el reason de overlay.
        if (!b) setBlockedReason("");
    }, []);

    const strike = useCallback(
        (why = "focus_lost") => {
            const now = Date.now();

            // gracia: no sumar, pero mantener overlay si sigue bloqueado (sin cambiar lastViolation)
            if (now < graceUntilRef.current) {
                applyBlocked();
                return countRef.current;
            }

            // anti-rebote
            if (now - lastStrikeRef.current < debounceMs) {
                return countRef.current;
            }
            lastStrikeRef.current = now;

            const b = computeBlocked();
            setBlocked(b);

            // ✅ Si NO está bloqueado, no sumes ni cambies reason
            if (!b) {
                setBlockedReason("");
                return countRef.current;
            }

            // ✅ bloqueo real: setear reasons consistentes
            setBlockedReason(why);
            setLastViolationReason(why);

            let next = countRef.current + 1;
            countRef.current = next;
            saveCount(next);

            const remaining = Math.max(0, maxViolations - next);

            try {
                onViolation?.({ count: next, remaining, reason: why });
            } catch { }

            if (next >= maxViolations) {
                try {
                    onTooManyViolations?.();
                } catch { }
            } else {
                try {
                    onAttentionRequired?.();
                } catch { }
            }

            return next;
        },
        [
            debounceMs,
            maxViolations,
            onViolation,
            onTooManyViolations,
            onAttentionRequired,
            saveCount,
            applyBlocked,
        ]
    );

    const manualStrike = useCallback(
        (why = "manual_violation", options = {}) => {
            const now = Date.now();

            const ignoreGrace = options?.ignoreGrace === true;
            const forceBlocked = options?.forceBlocked === true;
            const mode = options?.mode || "manual";

            const ignoreDebounce = options?.ignoreDebounce === true;

            if (!ignoreGrace && now < graceUntilRef.current) {
                applyBlocked();
                return countRef.current;
            }

            if (!ignoreDebounce && now - lastStrikeRef.current < debounceMs) {
                return countRef.current;
            }

            lastStrikeRef.current = now;

            if (forceBlocked) {
                setBlocked(true);
                setBlockedReason(why);
            }

            setLastViolationReason(why);

            const next = countRef.current + 1;
            countRef.current = next;
            saveCount(next);

            const remaining = Math.max(0, maxViolations - next);

            try {
                onViolation?.({
                    count: next,
                    remaining,
                    reason: why,
                    manual: true,
                    mode,
                });
            } catch { }

            if (next >= maxViolations) {
                try {
                    onTooManyViolations?.();
                } catch { }
            } else {
                try {
                    onAttentionRequired?.();
                } catch { }
            }

            return next;
        },
        [
            debounceMs,
            maxViolations,
            onViolation,
            onTooManyViolations,
            onAttentionRequired,
            saveCount,
            applyBlocked,
        ]
    );

    const forgive = useCallback(
        (n = 1) => {
            if (!Number.isFinite(n) || n <= 0) return countRef.current;
            let next = Math.max(0, countRef.current - n);
            countRef.current = next;
            saveCount(next);
            return next;
        },
        [saveCount]
    );

    const getCount = useCallback(() => countRef.current, []);
    const reset = useCallback(() => {
        countRef.current = 0;
        saveCount(0);
        lastStrikeRef.current = 0;
        lastRepeatStrikeRef.current = 0;
        setBlockedReason("");
        setLastViolationReason("");
    }, [saveCount]);

    const _rawRequestFullscreen = async (el) => {
        const opts = { navigationUI: "hide" };
        if (el.requestFullscreen) return el.requestFullscreen(opts).catch(() => el.requestFullscreen());
        if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
        if (el.mozRequestFullScreen) return el.mozRequestFullScreen();
        if (el.msRequestFullscreen) return el.msRequestFullscreen();
    };

    const requestFullscreen = useCallback(async () => {
        const el = document.documentElement || document.body;
        try {
            await _rawRequestFullscreen(el);
        } catch {
            try {
                await _rawRequestFullscreen(document.body);
            } catch { }
        }
        // ✅ no metas reason "request_fullscreen" que confunda el mensaje
        applyBlocked();
        grantGrace(1600);
    }, [applyBlocked, grantGrace]);

    const handleVisibility = useCallback(() => {
        if (!runningRef.current) return;

        clearFocusCheckTimer();

        if (document.hidden) {
            strike("visibility_hidden");
            return;
        }

        grantGrace(900);
        applyBlocked();
    }, [strike, applyBlocked, grantGrace]);

    const handleFocus = useCallback(() => {
        if (!runningRef.current) return;

        clearFocusCheckTimer();
        grantGrace(900);
        applyBlocked();
    }, [applyBlocked, grantGrace]);

    const handleBlur = useCallback(() => {
        if (!runningRef.current) return

        clearFocusCheckTimer()

        focusCheckTimerRef.current = setTimeout(() => {
            if (!runningRef.current) return

            const now = Date.now()

            if (now < graceUntilRef.current) {
                applyBlocked()
                return
            }

            const reallyHidden = document.hidden === true
            const reallyNoFocus = document.hasFocus?.() === false

            if (reallyHidden || reallyNoFocus) {
                strike(reallyHidden ? "visibility_hidden" : "window_blur")
                return
            }

            applyBlocked()
        }, 20)
    }, [strike, applyBlocked])

    const handleFullscreen = useCallback(() => {
        if (!runningRef.current) return;

        clearFocusCheckTimer();

        // ✅ Esperar a que el navegador estabilice el estado fullscreen.
        setTimeout(() => {
            if (!runningRef.current) return;

            const now = Date.now();
            if (now < graceUntilRef.current) {
                applyBlocked();
                return;
            }

            if (!isFullscreen()) {
                strike("fullscreen_exit");
                return;
            }

            grantGrace(900);
            applyBlocked();
        }, 500);
    }, [strike, applyBlocked, grantGrace]);

    const handlePageHide = useCallback(() => {
        if (!runningRef.current) return;
        if (countPageHide) strike("page_hide");
    }, [strike, countPageHide]);

    const keydownGuard = useCallback(
        (e) => {
            if (!runningRef.current) return;
            const ctrlOrMeta = e.ctrlKey || e.metaKey;
            const k = (e.key || "").toLowerCase();

            const navDev = ["t", "n", "w", "l", "p", "s", "u"];
            const devTools = (ctrlOrMeta && (e.shiftKey && ["i", "j"].includes(k))) || k === "f12";
            const clipboardOps = ctrlOrMeta && ["c", "x", "v", "a"].includes(k);

            if ((ctrlOrMeta && navDev.includes(k)) || devTools || clipboardOps) {
                if (preventDefaultOnHotkeys) {
                    try {
                        e.preventDefault();
                        e.stopPropagation();
                    } catch { }
                }
                strike("hotkey_navigation");
            }
        },
        [strike, preventDefaultOnHotkeys]
    );

    const heartbeatRef = useRef(null);
    const startHeartbeat = useCallback(() => {
        if (heartbeatRef.current) return;

        heartbeatRef.current = setInterval(() => {
            if (!runningRef.current) return;

            const now = Date.now();
            if (now < graceUntilRef.current) return;

            const b = computeBlocked();

            if (b) {
                // ✅ Mantiene el estado bloqueado, pero NO suma strikes automáticamente
                // cuando autoRepeatStrikes está desactivado.
                if (!autoRepeatStrikes) {
                    setBlocked(true);
                    return;
                }

                if (!lastRepeatStrikeRef.current) lastRepeatStrikeRef.current = now;

                const elapsed = now - lastRepeatStrikeRef.current;

                if (elapsed >= Math.max(2000, repeatStrikeMs)) {
                    lastRepeatStrikeRef.current = now;
                    strike(computeReason());
                    return;
                }

                setBlocked(true);
            } else {
                // volvió al modo seguro
                lastRepeatStrikeRef.current = 0;
                setBlocked(false);
                setBlockedReason("");
            }
        }, Math.max(2000, heartbeatMs));
    }, [heartbeatMs, repeatStrikeMs, autoRepeatStrikes, strike]);


    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    const handleResizeLikeFS = () => {
        if (!runningRef.current) return;
        applyBlocked();
    };

    const start = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;

        applyBlocked();

        window.addEventListener("resize", handleResizeLikeFS);
        window.addEventListener("orientationchange", handleResizeLikeFS);

        document.addEventListener("visibilitychange", handleVisibility);
        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);

        document.addEventListener("fullscreenchange", handleFullscreen);
        document.addEventListener("webkitfullscreenchange", handleFullscreen);
        document.addEventListener("mozfullscreenchange", handleFullscreen);
        document.addEventListener("MSFullscreenChange", handleFullscreen);

        window.addEventListener("pagehide", handlePageHide);
        document.addEventListener("keydown", keydownGuard, true);

        startHeartbeat();
    }, [
        applyBlocked,
        handleVisibility,
        handleFocus,
        handleBlur,
        handleFullscreen,
        handlePageHide,
        keydownGuard,
        startHeartbeat,
    ]);

    const stop = useCallback(
        async (clear = false) => {
            if (!runningRef.current) return;
            runningRef.current = false;

            window.removeEventListener("resize", handleResizeLikeFS);
            window.removeEventListener("orientationchange", handleResizeLikeFS);

            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);

            document.removeEventListener("fullscreenchange", handleFullscreen);
            document.removeEventListener("webkitfullscreenchange", handleFullscreen);
            document.removeEventListener("mozfullscreenchange", handleFullscreen);
            document.removeEventListener("MSFullscreenChange", handleFullscreen);

            window.removeEventListener("pagehide", handlePageHide);
            document.removeEventListener("keydown", keydownGuard, true);

            stopHeartbeat();
            clearFocusCheckTimer();

            if (clear) reset();

            setBlocked(false);
            setBlockedReason("");
            lastRepeatStrikeRef.current = 0; // ✅ nuevo
            // lastViolationReason NO se borra a menos que hagas reset(clear)
        },
        [handleVisibility, handleFocus, handleBlur, handleFullscreen, handlePageHide, keydownGuard, stopHeartbeat, reset]
    );

    useEffect(() => {
        applyBlocked();
    }, [applyBlocked]);

    return useMemo(
        () => ({
            blocked,
            // ✅ reason del overlay (bloqueo actual)
            reason: blockedReason,
            // ✅ última infracción real
            lastViolationReason,

            start,
            stop,
            strike,
            manualStrike,
            forgive,
            requestFullscreen,
            grantGrace,
            getCount,
            maxViolations,
            storageKey,
        }),
        [
            blocked,
            blockedReason,
            lastViolationReason,
            start,
            stop,
            strike,
            manualStrike,
            forgive,
            requestFullscreen,
            getCount,
            maxViolations,
            storageKey,
            grantGrace,
        ]
    );
}
